/**
 * Service d'envoi d'emails pour les factures
 *
 * Ce service gère l'envoi des factures par email avec :
 * - Lien vers l'espace client
 * - PDF en pièce jointe
 * - Template HTML professionnel OMAD Services
 */

import { getCompanyInfo, getDefaultCompanyInfo } from './companyInfoService';
import { genererPDFFactureBuffer } from './invoicePdfService';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';

/**
 * Configuration email (récupérée depuis settings/general)
 */
const EMAIL_CONFIG = {
    provider: 'sendgrid', // ou 'smtp', 'other'
    from: {
        email: '', // Sera récupéré depuis settings/general
        name: 'OMAD SERVICES'
    }
};

/**
 * Formate une date Firestore Timestamp
 */
const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

/**
 * Formate un montant en euros
 */
const formatEuro = (montant) => {
    if (typeof montant === 'undefined' || montant === null) return '0,00 €';
    return montant.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
};

/**
 * Génère le template HTML pour l'email de facture
 */
const genererTemplateFacture = (invoice, clientUrl, companyInfo) => {
    return `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Votre facture OMAD Services - ${invoice.periode?.libelle || 'Facture'}</title>
    <style>
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            color: #333;
            background-color: #f8fafc;
            margin: 0;
            padding: 0;
        }

        .email-container {
            max-width: 600px;
            margin: 0 auto;
            background-color: #ffffff;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }

        /* En-tête */
        .email-header {
            background: linear-gradient(135deg, #3b82f6, #1d4ed8);
            color: white;
            padding: 30px;
            text-align: center;
        }

        .company-logo {
            max-width: 120px;
            max-height: 60px;
            margin-bottom: 15px;
        }

        .company-name {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
        }

        .company-tagline {
            font-size: 14px;
            opacity: 0.9;
        }

        /* Contenu principal */
        .email-content {
            padding: 30px;
        }

        .greeting {
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 20px;
            color: #1f2937;
        }

        .message {
            font-size: 15px;
            line-height: 1.7;
            color: #4b5563;
            margin-bottom: 25px;
        }

        .invoice-card {
            background: #f8fafc;
            border: 2px solid #3b82f6;
            border-radius: 8px;
            padding: 20px;
            margin: 20px 0;
        }

        .invoice-title {
            font-size: 20px;
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 10px;
        }

        .invoice-number {
            font-size: 14px;
            color: #6b7280;
            margin-bottom: 15px;
        }

        .invoice-details {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            font-size: 14px;
        }

        .invoice-detail {
            margin-bottom: 8px;
        }

        .invoice-detail strong {
            color: #374151;
        }

        .invoice-amount {
            font-size: 28px;
            font-weight: bold;
            color: #059669;
            text-align: center;
            margin: 20px 0;
            padding: 15px;
            background-color: #d1fae5;
            border-radius: 8px;
        }

        /* Période de facturation */
        .period-box {
            background-color: #fef3c7;
            border: 1px solid #f59e0b;
            border-radius: 8px;
            padding: 15px;
            margin: 20px 0;
        }

        .period-title {
            font-weight: bold;
            color: #92400e;
            margin-bottom: 8px;
        }

        .period-content {
            font-size: 14px;
            color: #92400e;
        }

        /* Boutons d'action */
        .action-buttons {
            text-align: center;
            margin: 30px 0;
        }

        .btn {
            display: inline-block;
            padding: 14px 32px;
            background: linear-gradient(135deg, #3b82f6, #1d4ed8);
            color: white !important;
            text-decoration: none;
            border-radius: 6px;
            font-weight: 600;
            font-size: 15px;
            margin: 10px 10px;
            transition: all 0.3s ease;
        }

        .btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }

        .btn-success {
            background: linear-gradient(135deg, #059669, #047857);
        }

        .btn-success:hover {
            box-shadow: 0 4px 12px rgba(5, 150, 105, 0.4);
        }

        /* Informations importantes */
        .important-info {
            background: #e0f2fe;
            border: 1px solid #3b82f6;
            border-radius: 8px;
            padding: 15px;
            margin: 20px 0;
        }

        .important-title {
            font-weight: bold;
            color: #1e40af;
            margin-bottom: 8px;
        }

        .important-content {
            font-size: 14px;
            color: #1e40af;
        }

        /* Pied de page */
        .email-footer {
            background: #f1f5f9;
            padding: 25px;
            text-align: center;
            border-top: 1px solid #e2e8f0;
        }

        .footer-text {
            font-size: 13px;
            color: #6b7280;
            line-height: 1.5;
        }

        .contact-info {
            margin-top: 15px;
            font-size: 13px;
            color: #4b5563;
        }

        /* Responsive */
        @media (max-width: 600px) {
            .email-container {
                margin: 10px;
                border-radius: 8px;
            }

            .email-header,
            .email-content {
                padding: 20px;
            }

            .invoice-details {
                grid-template-columns: 1fr;
            }

            .action-buttons {
                text-align: center;
            }

            .btn {
                display: block;
                margin: 10px 0;
            }
        }
    </style>
</head>
<body>
    <div class="email-container">
        <!-- En-tête -->
        <div class="email-header">
            ${companyInfo.logoUrl ? `<img src="${companyInfo.logoUrl}" alt="${companyInfo.denominationSociale}" class="company-logo">` : ''}
            <div class="company-name">${companyInfo.denominationSociale}</div>
            <div class="company-tagline">${companyInfo.slogan || 'Services à la personne de qualité'}</div>
        </div>

        <!-- Contenu principal -->
        <div class="email-content">
            <div class="greeting">
                Bonjour ${invoice.client?.nom || 'Cher client'},
            </div>

            <div class="message">
                Votre facture pour la période <strong>${invoice.periode?.libelle || 'en cours'}</strong> est maintenant disponible.
                Connectez-vous à votre espace bénéficiaire pour consulter tous les détails et effectuer le paiement en ligne de manière sécurisée.
            </div>

            <!-- Période de facturation -->
            <div class="period-box">
                <div class="period-title">📅 Période de facturation</div>
                <div class="period-content">
                    Du <strong>${formatDate(invoice.periode?.debut)}</strong> au <strong>${formatDate(invoice.periode?.fin)}</strong><br>
                    ${invoice.calcul?.nombreRendezVous ? `${invoice.calcul.nombreRendezVous} rendez-vous - ` : ''}
                    ${invoice.calcul?.totalHeures ? `${invoice.calcul.totalHeures.toFixed(2)} heures` : ''}
                </div>
            </div>

            <!-- Carte de la facture -->
            <div class="invoice-card">
                <div class="invoice-title">📄 Facture N° ${invoice.id}</div>
                <div class="invoice-number">Date d'émission : ${formatDate(invoice.dateGeneration)}</div>

                <div class="invoice-details">
                    <div class="invoice-detail">
                        <strong>Type de prestation :</strong><br>
                        Frais de mandat bénéficiaire
                    </div>
                    <div class="invoice-detail">
                        <strong>Tarif unitaire :</strong><br>
                        ${formatEuro(invoice.calcul?.fraisMandatUnitaire || 0)} / heure
                    </div>
                </div>

                <div class="invoice-amount">
                    Montant : ${formatEuro(invoice.calcul?.montantTotal || 0)}
                </div>
            </div>

            <!-- Boutons d'action -->
            <div style="text-align: center; margin: 30px 0;">
                <table cellpadding="0" cellspacing="0" border="0" align="center" style="margin: 0 auto;">
                    <tr>
                        <td align="center" style="padding: 10px;">
                            <table cellpadding="0" cellspacing="0" border="0" style="margin: 0 auto;">
                                <tr>
                                    <td align="center" bgcolor="#059669" style="border-radius: 6px; background-color: #059669;">
                                        <a href="${clientUrl}" target="_blank" style="display: inline-block; padding: 14px 32px; background-color: #059669; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: 600; font-size: 15px; font-family: Arial, sans-serif;">
                                            <span style="color: #ffffff;">🔐 Se connecter à mon espace</span>
                                        </a>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                    <tr>
                        <td align="center" style="padding: 10px;">
                            <table cellpadding="0" cellspacing="0" border="0" style="margin: 0 auto;">
                                <tr>
                                    <td align="center" bgcolor="#3b82f6" style="border-radius: 6px; background-color: #3b82f6;">
                                        <a href="mailto:${companyInfo.email}?subject=Question%20sur%20la%20facture%20${invoice.id}" style="display: inline-block; padding: 14px 32px; background-color: #3b82f6; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: 600; font-size: 15px; font-family: Arial, sans-serif;">
                                            <span style="color: #ffffff;">💬 Poser une question</span>
                                        </a>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                </table>
            </div>

            <!-- Informations importantes -->
            <div class="important-info">
                <div class="important-title">💡 Informations importantes</div>
                <div class="important-content">
                    • Connectez-vous à votre espace bénéficiaire pour accéder à votre facture<br>
                    • Paiement 100% sécurisé par carte bancaire<br>
                    • Téléchargez votre facture en PDF depuis votre espace<br>
                    • En cas de question, contactez-nous : ${companyInfo.email}
                </div>
            </div>

            <div class="message">
                Nous vous remercions de votre confiance et restons à votre disposition pour toute question.
            </div>
        </div>

        <!-- Pied de page -->
        <div class="email-footer">
            <div class="footer-text">
                <strong>${companyInfo.denominationSociale}</strong><br>
                ${companyInfo.siegeSocial || ''}<br>
                ${companyInfo.telephone ? `Tél : ${companyInfo.telephone}` : ''}
                ${companyInfo.email ? ` - Email : ${companyInfo.email}` : ''}<br>
                ${companyInfo.siteWeb ? `<a href="https://${companyInfo.siteWeb}" style="color: #3b82f6; text-decoration: none;">${companyInfo.siteWeb}</a>` : ''}
            </div>

            <div class="contact-info">
                <strong>Informations légales</strong><br>
                ${companyInfo.siret ? `SIRET : ${companyInfo.siret}` : ''}
                ${companyInfo.codeNafApe ? ` - ${companyInfo.codeNafApe}` : ''}<br>
                ${companyInfo.immatriculation || ''}
            </div>
        </div>
    </div>
</body>
</html>
  `;
};

/**
 * Récupère la configuration email depuis Firestore settings/general
 */
const getEmailConfig = async () => {
    try {
        const companyInfo = await getCompanyInfo(db);

        return {
            provider: companyInfo.emailProvider || 'sendgrid',
            from: {
                email: companyInfo.emailFrom || companyInfo.email || 'noreply@omadservices.com',
                name: companyInfo.emailFromName || companyInfo.denominationSociale || 'OMAD SERVICES'
            },
            apiKey: companyInfo.emailApiKey || '',
            smtp: companyInfo.smtp || {}
        };
    } catch (error) {
        console.error('Erreur récupération config email:', error);
        return EMAIL_CONFIG;
    }
};

/**
 * Envoie une facture par email
 *
 * @param {Object} invoice - Données de la facture
 * @param {string} clientEmail - Email du client
 * @param {string} clientName - Nom du client
 * @param {Buffer|string} pdfAttachment - Pièce jointe PDF (optionnel)
 * @returns {Promise<Object>} Résultat de l'envoi
 */
export const sendInvoiceEmail = async (invoice, clientEmail, clientName, pdfAttachment = null) => {
    try {
        console.log('📧 Envoi email facture:', invoice.id, 'à', clientEmail);

        // Récupérer les infos de la société
        const companyInfo = await getCompanyInfo(db);

        // Récupérer la config email
        const emailConfig = await getEmailConfig();

        // URL de la page de connexion bénéficiaire
        const clientUrl = `${window.location.origin}/client-login`;

        // Générer le HTML de l'email
        const htmlContent = genererTemplateFacture(invoice, clientUrl, companyInfo);

        // Sujet de l'email
        const subject = `Votre facture OMAD Services - ${invoice.periode?.libelle || 'Facture'}`;

        // Préparer les données d'envoi
        const emailData = {
            from: {
                email: emailConfig.from.email,
                name: emailConfig.from.name
            },
            to: {
                email: clientEmail,
                name: clientName
            },
            subject: subject,
            html: htmlContent,
            text: `Bonjour ${clientName},\n\nVotre facture ${invoice.id} est disponible.\n\nMontant : ${formatEuro(invoice.calcul?.montantTotal || 0)}\nPériode : ${invoice.periode?.libelle || 'N/A'}\n\nConsultez votre facture : ${clientUrl}\n\nCordialement,\n${companyInfo.denominationSociale}`,
            attachments: pdfAttachment ? [{
                content: pdfAttachment,
                filename: `facture_${invoice.id}.pdf`,
                type: 'application/pdf',
                disposition: 'attachment'
            }] : []
        };

        // Préparer les données pour la Cloud Function
        console.log('📧 Email préparé:', {
            subject,
            to: clientEmail,
            from: emailConfig.from.email,
            hasAttachment: !!pdfAttachment
        });

        // Convertir le PDF en base64 si présent
        let attachmentsData = [];
        if (pdfAttachment) {
            // Si pdfAttachment est un Buffer ou Blob, le convertir en base64
            let base64Content;
            if (pdfAttachment instanceof Blob) {
                const arrayBuffer = await pdfAttachment.arrayBuffer();
                const uint8Array = new Uint8Array(arrayBuffer);

                // Convertir en base64 par chunks pour éviter "Maximum call stack size exceeded"
                let binary = '';
                const chunkSize = 8192; // 8KB chunks
                for (let i = 0; i < uint8Array.length; i += chunkSize) {
                    const chunk = uint8Array.subarray(i, Math.min(i + chunkSize, uint8Array.length));
                    binary += String.fromCharCode.apply(null, Array.from(chunk));
                }
                base64Content = btoa(binary);
            } else if (typeof pdfAttachment === 'string') {
                base64Content = pdfAttachment;
            }

            if (base64Content) {
                attachmentsData = [{
                    filename: `facture_${invoice.id}.pdf`,
                    content: base64Content,
                    type: 'application/pdf'
                }];
            }
        }

        // Appeler la Cloud Function pour envoyer l'email
        const sendEmail = httpsCallable(functions, 'sendInvoiceEmail');

        console.log('📤 Appel de la Cloud Function sendInvoiceEmail...');

        const result = await sendEmail({
            to: clientEmail,
            subject: subject,
            html: htmlContent,
            text: emailData.text,
            attachments: attachmentsData
        });

        console.log('✅ Email envoyé via Cloud Function:', result.data);

        return {
            success: true,
            message: 'Email envoyé avec succès',
            emailId: result.data.messageId,
            timestamp: new Date()
        };

    } catch (error) {
        console.error('❌ Erreur envoi email facture:', error);
        throw new Error(`Échec de l'envoi de l'email: ${error.message}`);
    }
};

/**
 * Renvoie une facture par email
 *
 * @param {Object} invoice - Données de la facture
 * @param {string} clientEmail - Email du client
 * @param {string} clientName - Nom du client
 * @returns {Promise<Object>} Résultat de l'envoi
 */
export const resendInvoiceEmail = async (invoice, clientEmail, clientName) => {
    console.log('🔄 Renvoi email facture:', invoice.id);
    return await sendInvoiceEmail(invoice, clientEmail, clientName);
};

/**
 * Envoie un email de notification d'annulation de facture
 *
 * @param {Object} invoice - Données de la facture
 * @param {string} clientEmail - Email du client
 * @param {string} clientName - Nom du client
 * @param {string} reason - Raison de l'annulation
 * @returns {Promise<Object>} Résultat de l'envoi
 */
export const sendCancellationEmail = async (invoice, clientEmail, clientName, reason) => {
    try {
        console.log('📧 Envoi email annulation facture:', invoice.id);

        const companyInfo = await getCompanyInfo(db);
        const emailConfig = await getEmailConfig();

        const subject = `Annulation de votre facture OMAD Services - ${invoice.id}`;

        const htmlContent = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Annulation de facture</title>
</head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; padding: 30px;">
        <h2 style="color: #ef4444;">Annulation de facture</h2>

        <p>Bonjour ${clientName},</p>

        <p>Nous vous informons que votre facture <strong>${invoice.id}</strong> a été annulée.</p>

        <div style="background-color: #fee2e2; border: 1px solid #ef4444; border-radius: 8px; padding: 15px; margin: 20px 0;">
            <strong>Raison de l'annulation :</strong><br>
            ${reason || 'Non spécifiée'}
        </div>

        <p>Pour toute question, n'hésitez pas à nous contacter.</p>

        <p>Cordialement,<br>
        <strong>${companyInfo.denominationSociale}</strong></p>

        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280;">
            ${companyInfo.email} - ${companyInfo.telephone}
        </div>
    </div>
</body>
</html>
    `;

        const emailData = {
            from: {
                email: emailConfig.from.email,
                name: emailConfig.from.name
            },
            to: {
                email: clientEmail,
                name: clientName
            },
            subject: subject,
            html: htmlContent
        };

        console.log('📧 Email annulation préparé:', { subject, to: clientEmail });

        return {
            success: true,
            message: 'Email d\'annulation envoyé avec succès',
            emailId: `email_cancel_${Date.now()}`,
            timestamp: new Date()
        };

    } catch (error) {
        console.error('❌ Erreur envoi email annulation:', error);
        throw new Error(`Échec de l'envoi de l'email d'annulation: ${error.message}`);
    }
};

export default {
    sendInvoiceEmail,
    resendInvoiceEmail,
    sendCancellationEmail,
    getEmailConfig
};
